/* Клиент API. Авторизация: Telegram initData → JWT (ТЗ 2). */

import { clearCache, invalidateAll } from "./cache.js";
import { initData, inTelegram, tgUser } from "./tg.js";

/* По умолчанию — относительный /api через прокси vite: один origin, HTTPS из туннеля,
   никакого CORS. Абсолютный адрес задаётся через VITE_API_URL. */
const BASE = import.meta.env.VITE_API_URL || "/api";
const TOKEN_KEY = "mera.token";
/* Кому выдан токен: id Telegram-аккаунта либо имя dev-участника.
   Без этой метки сохранённый токен пережил бы смену аккаунта — и человек
   видел бы чужой профиль. */
const OWNER_KEY = "mera.tokenOwner";

let token = localStorage.getItem(TOKEN_KEY) || null;
export const getToken = () => token;

/** Личность, под которой сейчас открыто приложение */
const currentOwner = (devUser) => (inTelegram ? `tg:${tgUser()?.id ?? ""}` : `dev:${devUser || ""}`);

export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

async function request(path, { method = "GET", body, query } = {}) {
  // BASE может быть относительным (/api) — тогда базой служит origin страницы
  const url = new URL(BASE + path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
    }
  }

  const res = await fetch(url, {
    method,
    headers: {
      ...(body ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  /* Запись могла задеть любую сводку — кэш GET-ответов помечаем устаревшим.
     Экраны продолжают показывать сохранённое, но перечитают его при открытии.
     Делаем это до разбора тела: удаление отвечает 204 без содержимого, и после
     выхода отсюда пометить кэш было бы уже негде — список рисовал бы удалённую
     запись до перезагрузки страницы. */
  if (method !== "GET" && res.ok) invalidateAll();

  if (res.status === 204) return null;

  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }

  if (!res.ok) {
    throw new ApiError(data?.error || `Ошибка ${res.status}`, res.status, data?.details);
  }
  return data;
}

/** Аватарки лежат на API и отдаются статикой — путь из базы дополняем базовым адресом */
export const avatarUrl = (path) => (!path ? null : path.startsWith("http") ? path : BASE + path);

/** Загрузка картинки: multipart, заголовок content-type ставит сам браузер */
export async function uploadImage(blob) {
  const form = new FormData();
  form.append("file", blob, "avatar.jpg");
  const res = await fetch(new URL(BASE + "/uploads", window.location.origin), {
    method: "POST",
    headers: token ? { authorization: "Bearer " + token } : {},
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(data?.error || ("Ошибка " + res.status), res.status);
  return data;
}

/** Вложение карточки: файл уходит как есть, сервер возвращает путь, имя и вид */
export async function uploadFile(file) {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await fetch(new URL(BASE + "/uploads/file", window.location.origin), {
    method: "POST",
    headers: token ? { authorization: "Bearer " + token } : {},
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(data?.error || ("Ошибка " + res.status), res.status);
  invalidateAll();
  return data;
}

/** Загрузка с отчётом об отправке: fetch про ход выгрузки молчит, XHR — нет.
    Нужна там, где файл большой и человеку важно видеть, что что-то происходит.
    Поля из `fields` уходят перед файлом — сервер разбирает поток по порядку. */
export function uploadWithProgress(path, file, { query, fields, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const url = new URL(BASE + path, window.location.origin);
    for (const [k, v] of Object.entries(query || {})) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    if (token) xhr.setRequestHeader("authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* сервер ответил не JSON — сообщение соберём из статуса */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        invalidateAll();
        resolve(data);
      } else reject(new ApiError(data?.error || `Ошибка ${xhr.status}`, xhr.status));
    };
    xhr.onerror = () => reject(new ApiError("Не получилось отправить файл", 0));

    const form = new FormData();
    for (const [k, v] of Object.entries(fields || {})) {
      if (v !== undefined && v !== null && v !== "") form.append(k, String(v));
    }
    form.append("file", file, file.name);
    xhr.send(form);
  });
}

export const api = {
  get: (p, query) => request(p, { query }),
  post: (p, body) => request(p, { method: "POST", body }),
  patch: (p, body) => request(p, { method: "PATCH", body }),
  del: (p, query) => request(p, { method: "DELETE", query }),
};

/** Логин: в Telegram — по initData, в браузере — dev-вход под выбранным участником */
export async function login(devUser = "Богдан") {
  const body = inTelegram ? { initData: initData() } : { as: devUser };
  const res = await request("/auth/telegram", { method: "POST", body });
  token = res.token;
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(OWNER_KEY, currentOwner(devUser));
  return res;
}

export function logout() {
  clearCache();
  token = null;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(OWNER_KEY);
}

/** Проверяет живой ли токен; если нет — логинится заново */
export async function ensureAuth(devUser) {
  // Токен от другого аккаунта не переиспользуем — иначе покажем чужой профиль
  if (token && localStorage.getItem(OWNER_KEY) !== currentOwner(devUser)) logout();

  if (token) {
    try {
      return await request("/me");
    } catch (e) {
      if (e.status !== 401 && e.status !== 403) throw e;
      logout();
    }
  }
  const res = await login(devUser);
  return res.user;
}
